import { type HTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// Re-authored from ../design-system/components/badges/Badge.jsx. success/warning/
// info/neutral palettes use arbitrary var(--ollio-*) — Badge is their only
// consumer, so they are intentionally not added to the @theme bridge.
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 font-sans font-semibold leading-none whitespace-nowrap border border-transparent [&_svg]:h-[13px] [&_svg]:w-[13px]',
  {
    variants: {
      variant: {
        neutral: 'bg-[var(--ollio-cream-200)] text-[var(--ollio-ink)] border-[var(--border-hairline)]',
        navy: 'bg-[var(--ollio-navy-light)] text-[var(--ollio-navy)]',
        gold: 'bg-[var(--ollio-gold-light)] text-[var(--ollio-gold-deep)]',
        success: 'bg-[var(--ollio-success-bg)] text-[var(--ollio-success)]',
        warning: 'bg-[var(--ollio-warning-bg)] text-[var(--ollio-warning)]',
        danger: 'bg-[var(--ollio-danger-bg)] text-destructive',
        info: 'bg-[var(--ollio-info-bg)] text-[var(--ollio-info)]',
        solid: 'bg-primary text-primary-foreground',
      },
      size: { md: 'text-xs px-2.5 py-1', sm: 'text-[0.6875rem] px-2 py-[3px]' },
      square: { true: 'rounded-sm', false: 'rounded-full' },
    },
    defaultVariants: { variant: 'neutral', size: 'md', square: false },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean;
  icon?: ReactNode;
}

export function Badge({ variant, size, square, dot = false, icon, className, children, ...rest }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant, size, square }), className)} {...rest}>
      {dot && <span className="h-1.5 w-1.5 flex-none rounded-full bg-current" />}
      {icon}
      {children}
    </span>
  );
}
